import React from 'react';
import { Card, CardContent, Typography, Avatar, Box } from '@mui/material';
import {
    HelpOutlineRounded, // Fallback icon

    // Top Row
    GroupsRounded as CommitteeIcon,
    BadgeRounded as MemberIcon,
    StorefrontRounded as ShopIcon,
    PersonOutlineRounded as PortalUserIcon,

    // Ecommerce Orders
    AssignmentOutlined as SubmittedIcon,
    FactCheckRounded as ConfirmIcon,
    Inventory2Rounded as ReadyToDeliverIcon,
    LocalShippingRounded as DeliveringIcon,
    CheckCircleRounded as CompleteIcon,
    CancelRounded as CancelIcon,
} from '@mui/icons-material';
import { formatVal } from "../../utils/string";

const STAT_CONFIG = {
    committee: {
        icon: CommitteeIcon,
        bgColor: '#F0EFFB',
        color: '#635BFF',
    },
    member: {
        icon: MemberIcon,
        bgColor: '#E6F4EA',
        color: '#10B981',
    },
    shop: {
        icon: ShopIcon,
        bgColor: '#FFF4E5',
        color: '#F97316',
    },
    portal_user: {
        icon: PortalUserIcon,
        bgColor: '#E0F2FE',
        color: '#0284C7',
    },
    // Ecommerce Order
    submitted: {
        icon: SubmittedIcon,
        bgColor: '#F0EFFB',
        color: '#635BFF',
    },
    confirm: {
        icon: ConfirmIcon,
        bgColor: '#F0EFFB',
        color: '#635BFF',
    },
    ready_to_deliver: {
        icon: ReadyToDeliverIcon,
        bgColor: '#FFF4E5',
        color: '#F97316',
    },
    delivering: {
        icon: DeliveringIcon,
        bgColor: '#E6F4EA',
        color: '#10B981',
    },
    complete: {
        icon: CompleteIcon,
        bgColor: '#E6F4EA',
        color: '#10B981',
    },
    cancel: {
        icon: CancelIcon,
        bgColor: '#FCE8E6',
        color: '#E53935',
    },
};

const DEFAULT_CONFIG = {
    icon: HelpOutlineRounded,
    bgColor: '#F3F4F6',
    color: '#6B7280',
};

// Helper function to recursively sum all numeric values
const calculateTotal = (val) => {
    if (val === null || val === undefined) return 0;

    // Handle single number or string
    if (typeof val === 'number') return Math.round(val);
    if (typeof val === 'string') return Math.round(parseFloat(val) || 0);

    // Handle Array (e.g. [10, 20, 30] or [{ a: 5 }, 15])
    if (Array.isArray(val)) {
        return val.reduce((acc, item) => acc + calculateTotal(item), 0);
    }

    // Handle Object (e.g. { draft: 10, settled: 20 })
    if (typeof val === 'object') {
        return Object.values(val).reduce((acc, item) => acc + calculateTotal(item), 0);
    }

    return 0;
};

export default function StatsBox({ title = '', value = 0, total = 0, showPercentage = false }) {
    const lookupKey = title.toLowerCase().replace(/\s+/g, '_');
    const config = STAT_CONFIG[lookupKey] || DEFAULT_CONFIG;
    const IconComponent = config.icon;
    const totalValue = calculateTotal(value);
    const percentageValue = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';

    return (
        <Card
            key={title}
            sx={{ display: 'flex', flexDirection: 'row', width: '100%', borderRadius: 3 }}
        >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4, mb: 1.5 }}>
                    <Avatar
                        sx={{
                            bgcolor: config.bgColor,
                            color: config.color,
                            width: 48,
                            height: 48,
                        }}
                    >
                        <IconComponent fontSize="large" />
                    </Avatar>

                    <Box>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, lineHeight: 1 }}
                        >
                            {title}
                        </Typography>

                        <Typography
                            sx={{
                                fontWeight: 700,
                                fontSize: '1rem',
                                wordBreak: 'break-word'
                            }}
                        >
                            {formatVal(totalValue, 'number')}
                            {showPercentage && <Box
                                component="span"
                                sx={{
                                    fontSize: '0.65rem',
                                    fontWeight: 500,
                                    color: 'success.main',
                                    marginLeft: 1
                                }}
                            >
                                ({percentageValue}%)
                            </Box>}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
